import React from 'react';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { IconButton, Input, cx, type InputProps } from '@/components/ui';
import { CountrySelectWithSearch } from './CountrySelectWithSearch';
import './PhoneInputAuth.css';

/**
 * Pieces shared by the login page (UserAuthentication), the sign-in popup (AuthModalHost)
 * and Reset password, so every sign-in surface renders the same controls.
 */

/** Sign-in method segment label: icon + name (the name is the tab's accessible name). */
export function AuthMethodLabel({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 [&>svg]:size-4 [&>svg]:shrink-0">
      {icon}
      {text}
    </span>
  );
}

/** Small heading above the phone / email forms ("Phone Login"). */
export function AuthSectionLabel({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <p className="m-0 flex items-center gap-2 font-ui text-small font-semibold text-primary [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-tertiary">
      {icon}
      {text}
    </p>
  );
}

export interface PasswordInputProps extends Omit<InputProps, 'type' | 'leading' | 'trailing'> {
  show: boolean;
  onToggleShow: () => void;
  /** Toggle without the click sound (screens whose toggle never played one). */
  silentToggle?: boolean;
}

/** Password Input with a lock icon and a show/hide IconButton. */
export function PasswordInput({ show, onToggleShow, silentToggle = false, ...rest }: PasswordInputProps) {
  const { t } = useTranslation();
  return (
    <Input
      {...rest}
      type={show ? 'text' : 'password'}
      leading={<Lock />}
      trailing={
        <IconButton
          label={show ? t('auth.hidePassword', 'Hide password') : t('auth.showPassword', 'Show password')}
          icon={show ? <EyeOff /> : <Eye />}
          variant="ghost"
          size="sm"
          silent={silentToggle}
          onClick={onToggleShow}
        />
      }
    />
  );
}

type PhoneInputProps = React.ComponentProps<typeof PhoneInput>;

export interface AuthPhoneInputProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  international?: boolean;
  countryOptionsOrder?: PhoneInputProps['countryOptionsOrder'];
  placeholder?: string;
  disabled?: boolean;
  /** Extra props for the number <input> (aria-label, required, autoComplete …). */
  numberInputProps?: Record<string, unknown>;
  /** Set by Field. */
  invalid?: boolean;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

const phoneShellClass =
  'flex h-11 min-w-0 items-center rounded-control border bg-inset px-3 font-ui text-body text-primary transition-colors duration-(--k-dur-fast) focus-within:border-focus focus-within:ring-3 focus-within:ring-focus/20';

/**
 * react-phone-number-input in an Input-like shell, with the searchable country picker.
 * Works as a Field child: Field's id, aria-describedby and invalid reach the number input.
 */
export function AuthPhoneInput({
  id,
  value,
  onChange,
  onBlur,
  international = false,
  countryOptionsOrder = ['US', 'CA', 'GB', '…'],
  placeholder,
  disabled,
  numberInputProps,
  invalid,
  'aria-describedby': describedBy,
  'aria-invalid': ariaInvalid,
}: AuthPhoneInputProps) {
  return (
    <div
      className={cx(
        phoneShellClass,
        invalid ? 'border-danger' : 'border-edge hover:border-edge-strong',
        disabled && 'border-dashed bg-transparent'
      )}
    >
      <PhoneInput
        id={id}
        international={international}
        defaultCountry="US"
        countryOptionsOrder={countryOptionsOrder}
        placeholder={placeholder}
        value={value || undefined}
        onChange={(v) => onChange(v ?? '')}
        onBlur={onBlur}
        countrySelectComponent={CountrySelectWithSearch}
        className="PhoneInputAuth w-full min-w-0"
        disabled={disabled}
        numberInputProps={{
          ...numberInputProps,
          ...(describedBy ? { 'aria-describedby': describedBy } : {}),
          ...(ariaInvalid || invalid ? { 'aria-invalid': true } : {}),
        }}
      />
    </div>
  );
}
